import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { openConversation } from "./conversation.js";
import { openCountryExpansionModal, openInvestmentOutlookModal, renderCountryExpansionPipeline } from "./core/government.js";
import { thUI } from "./threads.js";

/**
 * Government Relations as threads (replaces app.js renderGovernment).
 *
 * Left: your country threads (each visit, with its current step) and
 * invitations from investment-promotion agencies. Open a thread to read the
 * whole story: your trip, the meeting (each question and your answer), any
 * negotiation, and the official's formal offer letter with your decisions.
 * "Travel and meet" visits the country and walks straight into the meeting
 * (sections/conversation.js). Below: the market-entry tools that were on
 * the old screen (Investment Outlook, Country Expansion), now at the top.
 * Every letter carries a country file (the real economy, from the World Bank)
 * and the offer a 1-4 year commitment plan (2026-10-24).
 * Shared view: sections/threads.js. Data: /api/government/threads
 * (backend/government_threads.py).
 */

const GOV_DISMISS_KEY = "ges.gov.dismissed";

/** What you still owe on signed agreements: when, how much, how it's paid, and a button to pay early. */
function govPaymentsHtml(p) {
  if (!p || (!p.due.length && !p.recent.length)) return "";
  const m = (v) => fmtMoneyGov(v);
  const short = p.total_due > p.cash;
  return `<section class="sx-card gov-pay ${short ? "short" : ""}">
    <div class="gov-pay-head"><h3 class="sx-card-title">💳 Payments & commitments</h3>
      <div class="gov-pay-total"><span>Still to pay</span><b>${m(p.total_due)}</b><em>${short ? `⚠ more than your ${m(p.cash)} cash` : `cash ${m(p.cash)}`}</em></div></div>
    ${p.due.length ? `<table class="sx-table gov-pay-table"><tr><th>Country</th><th>Due</th><th class="num">Amount</th><th></th></tr>
      ${p.due.map((x) => `<tr class="${x.months_left <= 0 ? "now" : ""}"><td>${esc(x.country)}</td>
        <td>${x.months_left <= 0 ? "<b>At the start of next month</b>" : `Month ${x.due_month} (in ${x.months_left} month${x.months_left === 1 ? "" : "s"})`}</td>
        <td class="num">${m(x.amount)}</td><td><button type="button" data-pay-instalment="${x.id}">Pay now</button></td></tr>`).join("")}</table>`
      : `<p class="gov-pay-none">Nothing due: every agreement is paid up.</p>`}
    ${p.recent.length ? `<div class="gov-pay-recent">${p.recent.map((x) => `<span class="${x.status}">${x.status === "paid" ? "✓" : "✕"} ${esc(x.country)} ${m(x.amount)} ${x.status === "paid" ? `paid month ${x.paid_month}` : "missed"}</span>`).join("")}</div>` : ""}
    <p class="gov-pay-how">ⓘ ${esc(p.how)}</p></section>`;
}

/** The game's one money format for readouts (ui/kit.js uiMoney, compact: $2.40M). */
function fmtMoneyGov(v) {
  return uiMoney(v || 0, { compact: true });
}

/** The country file: the real economy (World Bank) and how your pledge compares with it. */
function govCountryFile(c) {
  if (!c) return "";
  const m = fmtMoneyGov;
  const g = c.governance || {};
  const bar = (label, v) => `<div class="gov-gbar"><span>${esc(label)}</span><div><i class="${v >= 60 ? "good" : v >= 40 ? "mid" : "bad"}" style="width:${Math.max(3, Math.min(100, v || 0))}%"></i></div><b>${Math.round(v || 0)}</b></div>`;
  const has = (v) => v !== null && v !== undefined;
  const pledge = c.pledge ? `<div class="gov-file-pledge">Your pledge of <b>${m(c.pledge)}</b> is <b>${c.pledge_pct_gdp.toFixed(c.pledge_pct_gdp < 0.1 ? 3 : 2)}%</b> of ${esc(c.country)}'s GDP${has(c.pledge_pct_fdi) ? ` and <b>${c.pledge_pct_fdi.toFixed(0)}%</b> of a year's foreign investment there` : ""}. The most it can take in one project is about <b>${m(c.max_project)}</b> a year.</div>` : "";
  return `<details class="gov-file" open><summary>🗂 Country file: ${esc(c.country)} <em>${esc(c.income_group)} · investment climate ${esc(c.climate)}</em></summary>
    <div class="gov-file-grid">
      <div><span>GDP</span><b>${m(c.gdp)}</b></div>
      <div><span>Per person</span><b>$${Math.round(c.gdp_per_capita).toLocaleString("en-US")}</b></div>
      <div><span>Population</span><b>${(c.population / 1e6).toFixed(c.population < 1e7 ? 2 : 1)}M</b></div>
      <div><span>Foreign investment a year</span><b>${has(c.fdi_inflow) ? m(c.fdi_inflow) : "—"}</b></div>
      <div><span>Labour force</span><b>${(c.labour_force / 1e6).toFixed(1)}M</b></div>
      <div><span>Online</span><b>${has(c.internet_pct) ? `${Math.round(c.internet_pct)}%` : "—"}</b></div>
      <div><span>Tax revenue</span><b>${c.tax_revenue_pct_gdp}% of GDP</b></div>
      <div><span>Engineers to hire</span><b>${Math.round(c.talent_pool).toLocaleString("en-US")}</b></div>
      ${has(c.hdi) ? `<div><span>Human development (UN)</span><b>${c.hdi.toFixed(3)}</b></div>` : ""}
      ${has(c.schooling_years) ? `<div><span>Years of schooling</span><b>${c.schooling_years}</b></div>` : ""}
      ${has(c.population_growth_pct) ? `<div><span>Population growth</span><b>${c.population_growth_pct.toFixed(1)}% a year</b></div>` : ""}
    </div>
    ${pledge}
    <div class="gov-gbars">${bar("Government effectiveness", g.gov_effectiveness)}${bar("Rule of law", g.rule_of_law)}${bar("Control of corruption", g.control_of_corruption)}${bar("Political stability", g.political_stability)}</div>
    <p class="gov-file-src">Source: ${esc(c.source)}${c.year ? `, ${c.year}` : ""}. Governance scores 0-100 (World Bank WGI); human development 0-1 (UNDP).</p></details>`;
}

function govPlanValue(type, v) {
  if (type === "Tax Holiday") return `your tax bill −${(v * 100).toFixed(1)}%`;
  if (type === "Cash Subsidy Grant") return `grant ${fmtMoneyGov(v)}`;
  return `+${(v * 100).toFixed(2)}% growth a month`;
}

/** Choose how many years to deliver the investment over: what you pay when, and what they give. */
function govPlanHtml(plan) {
  if (!plan) return "";
  return `<div class="gov-plan" data-gov-plan="${plan.deal_id}">
    <div class="gov-plan-head"><b>📅 Your commitment plan</b><span>Deliver it the way real investors do: a share on signing, then quarterly payments.</span></div>
    <div class="gov-plan-years" role="radiogroup" aria-label="Years to deliver the investment">${plan.options.map((x) => `
      <button type="button" role="radio" aria-checked="false" class="gov-year ${x.affordable ? "" : "short"}" data-gov-years="${x.years}">
        <b>${x.years} year${x.years > 1 ? "s" : ""}</b><em>${Math.round(x.upfront_pct * 100)}% now</em>${x.recommended ? `<i>Fits your cash</i>` : ""}</button>`).join("")}</div>
    <div class="gov-plan-body" id="gov-plan-body-${plan.deal_id}"></div></div>`;
}

function govPlanBody(plan, years) {
  const o = plan.options.find((x) => x.years === years);
  const perYear = Array.from({ length: years }, (_, k) => o.per_year + (k === 0 ? o.upfront : 0));
  const top = Math.max(...perYear);
  const bars = perYear.map((v, k) => `<div class="gov-plan-bar"><div><i style="height:${Math.max(6, (100 * v) / top).toFixed(0)}%"></i></div><span>Year ${k + 1}</span><b>${fmtMoneyGov(v)}</b></div>`).join("");
  const term = o.value_factor > 1 ? "+10% for paying fast" : o.value_factor < 1 ? `−${Math.round((1 - o.value_factor) * 100)}% for the slower plan` : "standard terms";
  return `<div class="gov-plan-grid">
      <div><span>On signing</span><b>${fmtMoneyGov(o.upfront)}</b>${o.affordable ? "<em>from your cash</em>" : `<em class="neg">more than your ${fmtMoneyGov(plan.cash)} cash</em>`}</div>
      <div><span>Then</span><b>${o.instalments} × ${fmtMoneyGov(o.each)}</b><em>every quarter</em></div>
      <div><span>They give</span><b>${esc(govPlanValue(plan.incentive_type, o.incentive_value))}</b><em>${term}</em></div>
    </div>
    <div class="gov-plan-bars" aria-label="Payments by year">${bars}</div>`;
}

/** Adds the country file and the plan to the open letter (threads.js decorate hook). */
function govDecorate(read, sel) {
  if (!read || !sel) return;
  const docs = sel.entries ? sel.entries.filter((e) => e.kind_entry === "doc") : [sel];
  const els = read.querySelectorAll(".doc");
  docs.forEach((d, i) => {
    const el = els[i];
    if (!el || (!d.country && !d.plan) || el.querySelector(".gov-file, .gov-plan")) return;  // never twice
    const anchor = el.querySelector(".doc-reply") || el.querySelector(".doc-body");
    if (!anchor) return;
    anchor.insertAdjacentHTML("beforebegin", govCountryFile(d.country) + govPlanHtml(d.plan));
    if (!d.plan) return;
    const sign = [...el.querySelectorAll(".doc-decision")].find((b) => (b.dataset.docAction || "").includes("finalize_government_deal"));
    const pick = (years) => {
      el.querySelectorAll("[data-gov-years]").forEach((b) => {
        const on = +b.dataset.govYears === years;
        b.classList.toggle("on", on);
        b.setAttribute("aria-checked", String(on));
      });
      el.querySelector(`#gov-plan-body-${d.plan.deal_id}`).innerHTML = govPlanBody(d.plan, years);
      const o = d.plan.options.find((x) => x.years === years);
      if (!sign) return;
      const action = JSON.parse(sign.dataset.docAction);
      action.params.years = years;
      sign.dataset.docAction = JSON.stringify(action);
      sign.disabled = !o.affordable;
      const why = sign.closest(".doc-choice").querySelector(".doc-why");
      if (why) why.textContent = o.affordable
        ? `${years}-year plan: ${fmtMoneyGov(o.upfront)} now, then ${o.instalments} quarterly payments of ${fmtMoneyGov(o.each)}.`
        : `Needs ${fmtMoneyGov(o.upfront)} on signing; you have ${fmtMoneyGov(d.plan.cash)}. Try a longer plan.`;
    };
    el.querySelectorAll("[data-gov-years]").forEach((b) => b.addEventListener("click", () => pick(+b.dataset.govYears)));
    pick(d.plan.chosen);
  });
}

function govDismissed() {
  try { return JSON.parse(localStorage.getItem(GOV_DISMISS_KEY) || "{}"); } catch (e) { return {}; }
}

window.renderGovernment = async function (main) {
  await thUI(main, {
    key: "gov",
    title: "Government Relations",
    subtitle: "Governments compete for your investment. Visit, make your case in the room, then read their offer and decide.",
    load: () => uiGet("/government/threads"),
    threadsTitle: "Open talks",
    itemsTitle: "Invitations",
    emptyThreads: "No talks waiting on you. Accept an invitation, or explore a country above.",
    emptyItems: "No new invitations this month.",
    emptyText: "Nothing on your desk.",
    summary: (d) => [
      { label: "Countries you operate in", value: d.summary.countries },
      { label: "Offers on the table", value: d.summary.open },
      { label: "Agreements signed", value: d.summary.signed },
    ],
    // Only talks still waiting on you: signed and declined ones drop off (the World map shows where you operate).
    threadsOf: (d) => d.threads.filter((t) => t.needs_you),
    itemsOf: (d) => { const g = govDismissed(); return d.invitations.filter((x) => g[x.id] !== d.month); },
    onDismiss: (id, d) => {
      const g = govDismissed();
      g[id] = d.month;
      try { localStorage.setItem(GOV_DISMISS_KEY, JSON.stringify(g)); } catch (e) { /* private mode */ }
    },
    decorate: (read, sel) => govDecorate(read, sel),
    onConverse: (dealId, rerender) => openConversation({ kind: "visit", refId: Number(dealId), onClose: rerender }),
    // After travelling, open that country's thread and walk into the meeting.
    afterAction: async (action) => {
      // Signed: the country opens in Global Holdings → Countries, to decide whether to invest more there.
      if (action.type === "finalize_government_deal") {
        const fresh = await uiGet("/government/threads");
        const t = fresh.threads.find((x) => x.deal_id === action.params.deal_id);
        if (t && window.ges) { window.__cpSelect = t.title; setTimeout(() => window.ges.showTab("countries"), 1200); }
        return null;
      }
      if (action.type !== "visit_country_leader") return null;
      const country = action.params.country_name;
      const fresh = await uiGet("/government/threads");
      const t = fresh.threads.find((x) => x.title === country && x.needs_you);
      if (t && typeof openConversation === "function") {
        setTimeout(() => openConversation({ kind: "visit", refId: t.deal_id }), 300);
      }
      return t ? { type: "thread", id: t.id } : null;
    },
    // Entering a new market comes first: it's the main move on this screen.
    top: (d) => `${govPaymentsHtml(d.payments)}
      <section class="sx-card gov-enter">
        <div class="gov-enter-head"><h3 class="sx-card-title">🌍 Enter a new market</h3>
          <div class="sx-card-sub">Commit capital to operate in a country: partnership, greenfield build, acquisition, joint venture or export. Any of the world's 196 countries.</div></div>
        <div class="gov-enter-actions"><button class="primary" id="btn-start-country-expansion">Explore a country →</button>
          <button id="btn-investment-outlook">Investment outlook</button></div>
        <div id="gov-outlook-result" style="margin-top:14px;"></div>
        <div id="country-expansion-pipeline" style="margin-top:14px;">Loading…</div>
      </section>`,
    footer: (d) => (d.data_source ? `<p class="gov-file-src gov-foot">Country figures: ${esc(d.data_source)}.</p>` : ""),
  });
  main.querySelector("#btn-start-country-expansion")?.addEventListener("click", () => openCountryExpansionModal());
  main.querySelectorAll("[data-pay-instalment]").forEach((b) => b.addEventListener("click", async () => {
    b.disabled = true;
    try { toast((await postJSON("/actions/execute", { action: { type: "gov_pay_instalment", params: { payment_id: Number(b.dataset.payInstalment) } } })).message); }
    catch (e) { toast(e.message, true); b.disabled = false; return; }
    await window.refreshAll();
  }));
  main.querySelector("#btn-investment-outlook")?.addEventListener("click", openInvestmentOutlookModal);
  if (typeof renderCountryExpansionPipeline === "function") renderCountryExpansionPipeline();
};
